import React, { useState, useEffect, FC, JSX } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import Button from '../ui/Button';
import Dropdown, { DropdownItem, DropdownDivider } from '../ui/Dropdown';
import { DropdownItemProps } from '../../types/components.d';
import logoImage from '../../assets/logo-top-left.svg';
import { LocalStorageUser } from '../../types/localStorage.d';

const Header: FC = (): JSX.Element => {
  const { user, logout, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [isMenuOpen, setIsMenuOpen] = useState<boolean>(false);
  const [isScrolled, setIsScrolled] = useState<boolean>(false);

  // Handle scroll effect
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleLogout = (): void => {
    logout();
    navigate('/');
  };

  const getDashboardLink = (): string => {
    if (!user) return '/login';
    switch (user.role) {
      case 'Jobseeker':
        return '/user/dashboard';
      case 'Company':
        return '/company/dashboard';
      case 'Agent':
        return '/agent/dashboard';
      default:
        return '/login';
    }
  };

  const isActive = (path: string): boolean => location.pathname === path;

  const navItems = [
    { name: 'Home', path: '/' },
    { name: 'Services', path: '/services' },
    { name: 'About', path: '/about' },
    { name: 'Contact', path: '/contact' },
  ];


  const socialMediaLinks = (
    <div className="flex flex-col items-left p-4 lg:flex-row space-y-4 lg:space-y-0 lg:space-x-4">
      {/* Instagram */}
      <a
        href="https://www.instagram.com/dhoondh.in?igsh=MWp0OXN5aGE2MWZuYw%3D%3D&utm_source=qr"
        target="_blank"
        rel="noopener noreferrer"
        className="flex items-center text-gray-500 hover:text-primary-lightGreen transition-colors"
        aria-label="Instagram"
      >
        <svg className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor">
          <path d="M7 2C4.243 2 2 4.243 2 7v10c0 2.757 2.243 5 5 5h10c2.757 0 5-2.243 5-5V7c0-2.757-2.243-5-5-5H7zm10 2c1.654 0 3 1.346 3 3v10c0 1.654-1.346 3-3 3H7c-1.654 0-3-1.346-3-3V7c0-1.654 1.346-3 3-3h10zm-5 3a6 6 0 100 12 6 6 0 000-12zm0 2a4 4 0 110 8 4 4 0 010-8zm6.5-.75a1.25 1.25 0 11-2.5 0 1.25 1.25 0 012.5 0z" />
        </svg>
        <span className='lg:hidden font-medium text-gray-700 hover:text-primary-lightGreen ml-2' >Instagram </span>
      </a>
      {/* WhatsApp */}
      <a
        href="https://wa.me/919316846322"
        target="_blank"
        rel="noopener noreferrer"
        className="flex items-center text-gray-500 hover:text-primary-lightGreen transition-colors"
        aria-label="WhatsApp"
      > 
        <svg className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor">
          <path d="M20.52 3.48A11.94 11.94 0 0012.06.03C5.64.03.45 5.22.45 11.64c0 2.05.54 4.05 1.57 5.82L.02 24l6.69-2.02a11.7 11.7 0 005.35 1.34h.01c6.43 0 11.62-5.19 11.62-11.61 0-3.1-1.21-6.02-3.17-8.23zM12.07 21.2h-.01a9.55 9.55 0 01-4.86-1.31l-.35-.2-3.97 1.2 1.18-3.86-.23-.36a9.6 9.6 0 01-1.45-5.23C2.38 6.41 6.45 2.34 12.06 2.34c2.57 0 4.98 1 6.8 2.82a9.53 9.53 0 012.8 6.78c0 5.61-4.57 10.26-10.59 9.26z" />
          <path d="M17.16 14.22c-.29-.14-1.72-.85-1.98-.95-.27-.1-.47-.14-.67.14-.2.29-.77.95-.94 1.15-.17.19-.35.21-.64.07-.29-.14-1.22-.45-2.33-1.44-.86-.77-1.44-1.72-1.6-2.01-.17-.29-.02-.45.12-.59.12-.12.29-.31.43-.46.15-.15.19-.26.29-.43.1-.19.05-.34-.02-.48-.07-.14-.67-1.62-.92-2.21-.24-.58-.48-.5-.67-.5l-.57-.01c-.2 0-.48.07-.73.34-.25.26-.96.94-.96 2.29s.98 2.66 1.12 2.85c.14.19 1.92 2.94 4.65 4.13.65.28 1.16.44 1.56.56.65.21 1.24.18 1.7.11.52-.08 1.72-.7 1.97-1.37.24-.67.24-1.23.17-1.37-.07-.14-.26-.22-.55-.36z" />
        </svg>
        <span className='lg:hidden font-medium text-gray-700 hover:text-primary-lightGreen ml-2' >WhatsApp </span>
      </a>
      {/* LinkedIn */}
      <a
        href="https://www.linkedin.com/company/dhoondh-ltd/?viewAsMember=true"
        target="_blank"
        rel="noopener noreferrer"
        className="flex items-center text-gray-500 hover:text-primary-lightGreen transition-colors"
        aria-label="LinkedIn"
      >
        <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
          <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
        </svg>
        <span className='lg:hidden font-medium text-gray-700 hover:text-primary-lightGreen ml-2' >LinkedIn </span>
      </a>
    </div>
  );
  return (
    <header 
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled 
          ? 'bg-white/90 backdrop-blur-xl shadow-lg border-b border-gray-200/50' 
          : 'bg-transparent backdrop-blur-none'
      }`}
    >
      <div className="max-w-7xl mx-auto">
        <div className="flex justify-between items-center h-16 lg:h-20 px-4 sm:px-6 lg:px-8">
          {/* Logo */}
          <div className="flex-shrink-0">
            <Link to="/" className="flex items-center group">
              <div className="relative">
                <img
                  src={logoImage}
                  alt="Dhoondh"
                  className="h-5 md:h-7 lg:h-10 aspect-auto object-contain transition-transform duration-300 group-hover:scale-105"
                />
                {/* Logo glow effect */}
                <div className="absolute inset-0 bg-primary-teaGreen/20 rounded-full blur-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300 -z-10"></div>
              </div>
            </Link>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-1">
            {navItems.map((item) => (
              <Link
                key={item.name}
                to={item.path}
                className={`relative px-4 py-2 text-sm font-medium rounded-xl transition-all duration-300 group ${
                  isActive(item.path)
                    ? 'text-primary-black bg-primary-teaGreen/20'
                    : 'text-gray-700 hover:text-primary-black hover:bg-primary-teaGreen/10'
                }`}
              >
                {item.name}
                {isActive(item.path) && (
                  <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 w-1 h-1 bg-primary-lightGreen rounded-full"></div>
                )}
              </Link>
            ))}
          </nav>

          {/* Desktop Auth Section */}
          <div className="hidden lg:flex items-center space-x-3">
            {isAuthenticated ? (
              <div className="flex items-center space-x-3">
                {socialMediaLinks}
                <Dropdown
                  trigger={
                    <button className="flex items-center space-x-2 text-sm font-medium text-gray-700 hover:text-primary-black focus:outline-none rounded-xl p-2 hover:bg-primary-teaGreen/10 transition-all duration-300 group">
                      <div className="w-8 h-8 bg-gradient-to-br from-primary-lightGreen to-primary-teaGreen rounded-full flex items-center justify-center shadow-lg group-hover:shadow-xl transition-shadow duration-300">
                        <span className="text-primary-black font-semibold text-sm">
                          {user?.firstName?.charAt(0) || user?.email?.charAt(0) || 'U'}
                        </span>
                      </div>
                      <div className="text-left">
                        <div className="font-medium">{user?.firstName || user?.email}</div>
                        <div className="text-xs text-gray-500 capitalize">{user?.role}</div>
                      </div>
                      <svg className="h-4 w-4 transition-transform duration-300 group-hover:rotate-180" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                      </svg>
                    </button>
                  }
                  position="bottom-right"
                >
                  <DropdownItem
                    onClick={() => navigate(getDashboardLink())}
                    icon={
                      <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2H5a2 2 0 00-2-2z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 5a2 2 0 012-2h4a2 2 0 012 2v2H8V5z" />
                      </svg>
                    }
                  >
                    Dashboard
                  </DropdownItem>
                  <DropdownDivider />
                  <DropdownItem
                    onClick={handleLogout}
                    danger
                    icon={
                      <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                      </svg>
                    }
                  >
                    Logout
                  </DropdownItem>
                </Dropdown>
              </div>
            ) : (
              <div className="flex items-center space-x-3">
                
                <Link to="/login">
                  <Button variant="outline" size="sm">
                    Login
                  </Button>
                </Link>
                <Link to="/register">
                  <Button size="sm" className="px-6 h-[40px]">
                    Get Started
                  </Button>
                </Link>
                {socialMediaLinks}
              </div>
            )}
          </div>

          {/* Mobile menu button */}
          <div className="lg:hidden">
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="relative p-2 rounded-xl text-gray-700 hover:text-primary-black hover:bg-primary-teaGreen/10 focus:outline-none focus:ring-2 focus:ring-primary-lightGreen transition-all duration-300"
            >
              <span className="sr-only">Open main menu</span>
              <div className="w-6 h-6 flex flex-col justify-center items-center">
                <span className={`block h-0.5 w-6 bg-current transition-all duration-300 ${isMenuOpen ? 'rotate-45 translate-y-1' : '-translate-y-1'}`}></span>
                <span className={`block h-0.5 w-6 bg-current transition-all duration-300 ${isMenuOpen ? 'opacity-0' : 'opacity-100'}`}></span>
                <span className={`block h-0.5 w-6 bg-current transition-all duration-300 ${isMenuOpen ? '-rotate-45 -translate-y-1' : 'translate-y-1'}`}></span>
              </div>
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        <div className={`lg:hidden transition-all duration-300 overflow-hidden ${
          isMenuOpen ? 'max-h-screen opacity-100' : 'max-h-0 opacity-0'
        }`}>
          <div className="px-4 pt-2 pb-4 space-y-1 bg-white/95 backdrop-blur-xl border-t border-gray-200/50 shadow-lg">
            {/* Mobile Nav Items */}
            {navItems.map((item) => (
              <Link
                key={item.name}
                to={item.path}
                className={`block px-4 py-3 text-base font-medium rounded-xl transition-all duration-300 ${
                  isActive(item.path)
                    ? 'text-primary-black bg-primary-teaGreen/20'
                    : 'text-gray-700 hover:text-primary-black hover:bg-primary-teaGreen/10'
                }`}
                onClick={() => setIsMenuOpen(false)}
              >
                {item.name}
              </Link>
            ))}

              {socialMediaLinks}
            {/* Mobile Auth Section */}
            <div className="pt-4 border-t border-gray-200">
              {isAuthenticated ? (
                <div className="space-y-3">
                  <div className="flex items-center px-4 py-3 space-x-3 bg-primary-teaGreen/10 rounded-xl">
                    <div className="w-10 h-10 bg-gradient-to-br from-primary-lightGreen to-primary-teaGreen rounded-full flex items-center justify-center shadow-lg">
                      <span className="text-primary-black font-semibold">
                        {user?.firstName?.charAt(0) || user?.email?.charAt(0) || 'U'}
                      </span>
                    </div>
                    <div>
                      <div className="font-medium text-primary-black">
                        {user?.firstName || user?.email}
                      </div>
                      <div className="text-sm text-gray-500 capitalize">{user?.role}</div>
                    </div>
                  </div>
                  <Link
                    to={getDashboardLink()}
                    className="flex items-center space-x-3 px-4 py-3 text-base font-medium text-gray-700 hover:text-primary-black hover:bg-primary-teaGreen/10 rounded-xl transition-all duration-300"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2H5a2 2 0 00-2-2z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 5a2 2 0 012-2h4a2 2 0 012 2v2H8V5z" />
                    </svg>
                    <span>Dashboard</span>
                  </Link>
                  <button
                    onClick={() => {
                      handleLogout();
                      setIsMenuOpen(false);
                    }}
                    className="flex items-center space-x-3 px-4 py-3 text-base font-medium text-red-600 hover:text-red-700 hover:bg-red-50 rounded-xl transition-all duration-300 w-full text-left"
                  >
                    <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                    </svg>
                    <span>Logout</span>
                  </button>
                </div>
              ) : (
                <div className="space-y-3">
                  <Link
                    to="/login"
                    className="block px-4 py-3 text-base font-medium text-gray-700 hover:text-primary-black hover:bg-primary-teaGreen/10 rounded-xl transition-all duration-300"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    Login
                  </Link>
                  <Link
                    to="/register"
                    className="block px-4 py-3 text-base font-medium text-white bg-primary-lightGreen hover:bg-primary-lightGreen/90 rounded-xl transition-all duration-300 text-center"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    Get Started
                  </Link>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
